import {
  createLocalMediaStateEvent,
  type AmnOutgoingEvent,
} from "./outgoingEvents"
import type {
  GetLocalMediaStateEvent,
  SetLocalMediaEvent,
} from "./incomingEvents"
import {
  getIsAudioMuted,
  setIsAudioMuted,
  getIsVideoDisabled,
  setIsVideoDisabled,
  whenMuteChanged,
  whenVideoStateChanged,
} from "../../../ucs"

export type LocalMediaEvents = {
  handleGetLocalMediaStateEvent: (event: GetLocalMediaStateEvent) => void
  handleSetLocalMediaEvent: (event: SetLocalMediaEvent) => void
  dispose: () => void
}

export const initLocalMediaEvents = (
  postMessage: (event: AmnOutgoingEvent) => void
): LocalMediaEvents => {
  const sendLocalMediaState = (
    mediaType: "audio" | "video",
    enabled: boolean,
    failureMessage: string | null = null
  ) => {
    const event = createLocalMediaStateEvent(mediaType, enabled, failureMessage)
    postMessage(event)
  }

  const handleGetLocalMediaStateEvent = async (
    event: GetLocalMediaStateEvent
  ) => {
    const { mediaType } = event.payload

    try {
      if (mediaType === "audio") {
        const isMuted = await getIsAudioMuted()
        sendLocalMediaState("audio", !isMuted)
      } else if (mediaType === "video") {
        const isDisabled = await getIsVideoDisabled()
        sendLocalMediaState("video", !isDisabled)
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to get media state"
      // eslint-disable-next-line no-console
      console.error("Error getting media state:", { error })
      sendLocalMediaState(mediaType, false, errorMessage)
    }
  }

  const handleSetLocalMediaEvent = async (event: SetLocalMediaEvent) => {
    const { mediaType, enabled } = event.payload

    try {
      if (mediaType === "audio") {
        await setIsAudioMuted(!enabled)
      } else if (mediaType === "video") {
        await setIsVideoDisabled(!enabled)
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to set media state"
      // eslint-disable-next-line no-console
      console.error("Error setting media state:", { error })

      try {
        if (mediaType === "audio") {
          const isMuted = await getIsAudioMuted()
          sendLocalMediaState("audio", !isMuted, errorMessage)
        } else if (mediaType === "video") {
          const isDisabled = await getIsVideoDisabled()
          sendLocalMediaState("video", !isDisabled, errorMessage)
        }
      } catch {
        sendLocalMediaState(mediaType, false, errorMessage)
      }
    }
  }

  const unwatchMuteChanged = whenMuteChanged(({ memberAgent }) => {
    if (!memberAgent.isLocal) return
    if (!memberAgent.participantAgent) return
    const { isMuted } = memberAgent.participantAgent
    sendLocalMediaState("audio", !isMuted)
  })

  const unwatchVideoStateChanged = whenVideoStateChanged(
    ({ memberAgent, mediaStream }) => {
      if (!memberAgent.isLocal) return
      if (!mediaStream) return
      sendLocalMediaState("video", mediaStream.isVideoEnabled())
    }
  )

  const dispose = () => {
    unwatchMuteChanged()
    unwatchVideoStateChanged()
  }

  return {
    handleGetLocalMediaStateEvent,
    handleSetLocalMediaEvent,
    dispose,
  }
}
